import {ConnectedFocusError} from 'focus-formik-error'
import {Field, Form, Formik} from 'formik'
import moment from 'moment'
import {RefObject, useRef, useState} from 'react'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import Flatpickr from 'react-flatpickr'
import {Crop} from 'react-image-crop'
import {useIntl} from 'react-intl'
import {NumericFormat} from 'react-number-format'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import Select from 'react-select'
import Toolbar, {
  formats,
  modules,
  modules_2,
  modules_3,
  modules_4,
  modules_5,
  modules_6,
  QuillToolbar2,
  QuillToolbar3,
  QuillToolbar4,
  QuillToolbar5,
  QuillToolbar6,
} from 'src/app/context/QuillConfig'
import {Tabs} from 'src/feature/bite/component'
import CropAspect from 'src/feature/bite/hook/useCropAspect'
import MembersByItem from 'src/feature/members/view/MembersByItem'
import {useGetDetail} from 'src/feature/package/context/useQuery'
import {useGetDetailTermsAndConditions} from 'src/feature/store/context/useQuery'
import * as Yup from 'yup'
import {useUpdateItem} from '../../context/useMutation'
import {
  useGetItemCategoryList,
  useGetItemSubCategoryList,
  useGetItemTypeList,
} from '../../context/useQuery'
import {useItem} from '../../hook'

export default function ItemForm(): JSX.Element {
  const intl = useIntl()
  const {isLoading} = useGetDetail()
  const {isLoading: isLoadingCategories} = useGetItemCategoryList()
  const {isLoading: isLoadingSubCategories} = useGetItemSubCategoryList()
  const {data: itemTypes, isLoading: isFetchingItemTypes} = useGetItemTypeList()
  const {mutateAsync, isLoading: isSaving} = useUpdateItem()
  const {detail, detailSet, categories, subCategories} = useItem()
  const {data: isTermsAndConditionsAvailable} = useGetDetailTermsAndConditions()

  const [activeTab, setActiveTab] = useState('Overview')

  const onSubmit = async (values, {setSubmitting, resetForm}) => {
    values.taxRate = 5

    if (values.directSale === false) {
      values.price = 0
      values.purchaseType = 0
      values.eStore = false
      values.paymentLink = false
      values.imagesData = null
    }

    if (values.isSubscriptionPackage === false) {
      values.usageLimit = 1
      values.duration = 1
    }

    values.termsAndConditions = values.infoContentData.find(
      (item) => item.name === 'Terms and Conditions'
    )?.data

    values.infoContentData = values.infoContentData.filter(
      (item) => item.name !== 'Terms and Conditions'
    )

    mutateAsync({
      ...values,
    })
    setSubmitting(false)
  }

  const status = [
    {value: 1, label: intl.formatMessage({id: 'ACTIVE'})},
    {value: 2, label: intl.formatMessage({id: 'INACTIVE'})},
  ]

  const mockUpPurchaseTypeSelect = [
    {value: 1, label: 'Vasco App'},
    // { value: 1, label: 'Online' },
    // { value: 2, label: 'In Store' },
    // { value: 3, label: 'Both' },
  ]

  const validSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    // detail: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    detail: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test('description', 'Description must be less than 1000 characters', function (value) {
        const removedTags = value?.replace(/<[^>]*>/g, '')

        if (removedTags.length > descriptionLimit) {
          return this.createError({
            path: this.path,
            message: 'Description must be less than 1000 characters',
          })
        }
        return true
      }),
    usageLimit: Yup.number().when(['isSubscriptionPackage'], ([isSubscriptionPackage], schema) => {
      return isSubscriptionPackage
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema.nullable()
    }),
    categoryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    duration: Yup.number().when(['isSubscriptionPackage'], ([isSubscriptionPackage], schema) => {
      return isSubscriptionPackage
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema.nullable()
    }),
    subCategoryID: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    availableFrom: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    isSubscriptionPackage: Yup.boolean().optional(),
    availableUntil: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test('availableUntil', 'End Date must be greater than Publish Date', function (value) {
        const {availableFrom} = this.parent
        const availableFromMoment = moment(availableFrom, 'DD-MM-YYYY HH:mm')
        const availableUntilMoment = moment(value, 'DD-MM-YYYY HH:mm')

        if (availableUntilMoment.isSameOrBefore(availableFromMoment)) {
          return this.createError({
            path: this.path,
            message: 'End Date must be greater than Publish Date',
          })
        } else {
          return true
        }
      }),
    sku: Yup.string(),
    barcode: Yup.string(),
    itemTypeID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    price: Yup.number().when(['directSale'], ([directSale], schema) => {
      return directSale
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1}))
        : schema
            .nullable()
            .min(-9999, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -9999}))
    }),
    taxRate: Yup.number().optional().nullable(),
    purchaseType: Yup.number().when(['directSale'], ([directSale], schema) => {
      return directSale
        ? schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
        : schema.nullable()
    }),
    directSale: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    eStore: Yup.boolean().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    paymentLink: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    infoContentData: Yup.array(
      Yup.object({
        id: Yup.string().optional().nullable(),
        name: Yup.string().optional().nullable(),
        contentURL: Yup.string().optional().nullable(),
        data: Yup.string()
          .optional()
          .nullable()
          .test('data', 'Data must be less than 1000 characters', function (value) {
            const removedTags = value?.replace(/<[^>]*>/g, '')
            if (removedTags && removedTags?.length > 1000) return false
            return true
          }),
      })
        .optional()
        .nullable()
    )
      .optional()
      .nullable(),
    imagesData: Yup.array()
      .test(
        'image',
        intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
        (value, context) => {
          if ((value?.length === 0 || !value) && context.parent.directSale) {
            return false
          }

          return true
        }
      )
      .nullable(),
  })

  const [message, setMessage] = useState<string>('')

  const descriptionLimit = 1000
  const [isRichTextCharacterLimitExceeded, setIsRichTextCharacterLimitExceeded] =
    useState<boolean>(false)

  const reactQuillRef: RefObject<ReactQuill> = useRef<ReactQuill>(null)

  const checkCharacterCount = (event: any) => {
    const unprivilegedEditor = (reactQuillRef?.current as any)?.unprivilegedEditor
    if (unprivilegedEditor.getLength() > descriptionLimit + 1 && event.key !== 'Backspace')
      event.preventDefault()
  }

  const calculateCharacterCount = (value: string) => {
    const removedTags = value?.replace(/<[^>]*>/g, '')
    if (removedTags.length > descriptionLimit) setIsRichTextCharacterLimitExceeded(true)
    else setIsRichTextCharacterLimitExceeded(false)
  }

  // tooltips ve faqs array'lerini koşullu olarak oluştur
  let tooltips = [
    {
      title: 'Feature and Benefits',
      content:
        'Provide a detailed overview of the product’s features and benefits, including what customers will gain from purchasing.',
    },
    {
      title: 'Eligibility',
      content:
        'Define any specific requirements or conditions that customers must meet to be eligible for this product.',
    },
    {
      title: 'Additional Benefits',
      content: 'List any extra perks or advantages that come with this product.',
    },

    {
      title: 'Terms and Conditions',
      content:
        'Include any specific terms and conditions that apply to the purchase of this product, such as usage rights or restrictions.',
    },

    {
      title: 'Cancellation and Refund Policy',
      content:
        'Clearly outline the terms and conditions related to canceling the purchase, including any applicable fees or restrictions.',
    },
  ]

  let faqs = [
    {
      title: 'Feature and Benefits',
      content: intl.formatMessage({id: 'AUTH.SUBSCRIPTION.FAQ_1_CONTENT'}),
      order: 0,
    },
    {
      title: 'Eligibility',
      content: intl.formatMessage({id: 'AUTH.SUBSCRIPTION.FAQ_2_CONTENT'}),
      order: 1,
    },
    {
      title: 'Additional Benefits',
      content: intl.formatMessage({id: 'AUTH.SUBSCRIPTION.FAQ_3_CONTENT'}),
      order: 2,
    },

    {
      title: 'Terms and Conditions',
      content: intl.formatMessage({id: 'AUTH.SUBSCRIPTION.FAQ_10_CONTENT'}),
      order: 4,
    },

    {
      title: 'Cancellation and Refund Policy',
      content: intl.formatMessage({id: 'AUTH.SUBSCRIPTION.FAQ_4_CONTENT'}),
      order: 3,
    },
  ]

  const tabs = [
    {
      label: intl.formatMessage({id: 'MEMBER.DETAIL.OVERVIEW'}),
      key: 'Overview',
      component: <ProductDetailOverview />,
    },
    {
      label: 'Purchased By',
      key: 'Purchased',
      component: <MembersWhoPurchased />,
    },
  ]

  function findSelectedTab(tabs: any[], selectedTab: string) {
    for (const tab of tabs) {
      if (tab.key === selectedTab) {
        return tab
      } else if (tab.childs) {
        const selectedChildTab = findSelectedTab(tab.childs, selectedTab)

        if (selectedChildTab) {
          return selectedChildTab
        }
      }
    }
    return null
  }

  return (
    <>
      {!detail?.isEditable && (
        <div className='card mb-5 mb-xl-10 mt-10'>
          <div className='card-body pt-9 pb-0'>
            <div className='d-flex flex-wrap flex-sm-nowrap'>
              <div className='flex-grow-1'>
                <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                  <div className='d-flex flex-column' style={{width: '30%'}}>
                    <div className='page-title d-flex justify-content-between gap-5 me-3 align-items-center'>
                      <h1 className='page-heading d-flex flex-column justify-content-center text-dark fw-bold fs-3 m-0'>
                        {activeTab === 'Overview' ? 'Product Details' : 'Purchased By'}
                      </h1>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Tabs
              tabs={tabs}
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              loading={isLoading}
            />
          </div>
        </div>
      )}
      <div className='tab-content' id='myTabContent'>
        <div className='tab-pane fade active show' role='tabpanel'>
          <div className='card-body pt-0'>
            <div className='tab-content' id='myTabContent'>
              {findSelectedTab(tabs, activeTab)?.component}
            </div>
          </div>
        </div>
      </div>
    </>
  )

  function ProductDetailOverview() {
    const [crop, setCrop] = useState<Crop>()
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)

    const [image1, setImage1] = useState(null)
    const [tempImage, setTempImage] = useState('')
    const [resizeImageModal, setResizeImageModal] = useState(false)

    const handleImageUpload = (index: number) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.png, .jpg, .jpeg'
      input.onchange = (e: any) => {
        if (e.target.files && e.target.files.length > 0) {
          setCrop(undefined)
          const reader = new FileReader()
          reader.addEventListener('load', () => {
            setTempImage(reader.result?.toString() || '')
            setResizeImageModal(true)
            setSelectedImageIndex(index)
          })
          reader.readAsDataURL(e.target.files[0])
        }
      }
      input.click()
    }

    return (
      <Formik
        initialValues={{
          name: detail?.name || '',
          detail: detail?.detail || '',
          isEditable: detail?.isEditable,
          isSubscriptionPackage: detail?.isSubscriptionPackage || false,
          categoryID: detail?.categoryID || '',
          usageLimit: detail?.usageLimit || 1,
          duration: detail?.duration || null,
          subCategoryID: detail?.subCategoryID || '',
          status: detail?.status || 1,
          availableFrom: moment.utc(detail?.availableFrom).format('DD-MM-YYYY HH:mm') || '',
          availableUntil: moment.utc(detail?.availableUntil).format('DD-MM-YYYY HH:mm') || '',
          sku: detail?.sku || '',
          barcode: detail?.barcode || '',
          itemTypeID: detail?.itemTypeID || 1,
          price: detail?.price || 0.0,
          taxRate: detail?.taxRate || '5',
          purchaseType: detail?.purchaseType || 1,
          directSale: detail?.directSale,
          eStore: !detail?.directSale ? false : detail?.eStore,
          paymentLink: !detail?.directSale ? false : detail?.paymentLink,
          infoContentData: detail?.infoContentData || [],
          imagesData: !detail?.directSale ? null : detail?.imagesData,
        }}
        validationSchema={validSchema}
        onSubmit={onSubmit}
      >
        {(prop: any) => {
          return (
            <>
              <Modal
                id='kt_modal_add_product_img'
                show={resizeImageModal}
                onHide={() => {
                  setResizeImageModal(false)
                }}
                size='lg'
                centered
                backdrop='static'
              >
                <Modal.Header closeButton>
                  <Modal.Title>Crop Image</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {tempImage && (
                    <div className='d-flex flex-column align-items-center'>
                      <CropAspect
                        values={prop.values}
                        crop={crop}
                        setCrop={setCrop}
                        imgSrc={tempImage}
                        setImgSrc={setTempImage}
                        selectedImageIndex={selectedImageIndex}
                        setResizeImageModal={setResizeImageModal}
                        setImage1={setImage1}
                        setFieldValue={prop.setFieldValue}
                      />
                    </div>
                  )}
                </Modal.Body>
              </Modal>

              <Form className='form d-flex flex-column flex-lg-row fv-plugins-bootstrap5 fv-plugins-framework'>
                <ConnectedFocusError />
                <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-12'>
                  <div className='tab-content'>
                    <div
                      className='tab-pane fade active show'
                      id='kt_ecommerce_add_product_general'
                      role='tabpanel'
                    >
                      <div className='d-flex flex-column gap-7 gap-lg-10'>
                        <div className='card card-flush py-4'>
                          <div className='card-header'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>
                                  {intl.formatMessage({id: 'ITEM.INFO'})}
                                </h3>
                                <p className='text-muted fw-normal fs-6'>
                                  Provide the key details of the product you want to create.
                                  Products can be services or products and can be sold individually
                                  or included in memberships.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className='card-body pt-0'>
                            <div className='fv-row fv-plugins-icon-container'>
                              <div className='row mb-3'>
                                <div className='col-12'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'ITEM.NAME'})}
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Enter a clear and descriptive name for the service or
                                          product.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <Field
                                    type='text'
                                    disabled={!prop.values.isEditable}
                                    isDisabled={!prop.values.isEditable}
                                    name='name'
                                    id='name'
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'ITEM.NAME',
                                    })}
                                    maxLength={51}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.name && prop.touched.name && (
                                      <div className='fv-help-block'>{prop?.errors.name}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className='row mb-3'>
                                <div className='col-12'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({
                                      id: 'ITEM.DESCRIPTION',
                                    })}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Provide a detailed description, including key features,
                                          benefits, or details about the service or product.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <Toolbar />
                                  <ReactQuill
                                    readOnly={!prop.values.isEditable}
                                    placeholder={intl.formatMessage({id: 'DESCRIPTION'})}
                                    value={prop.values.detail}
                                    formats={formats}
                                    modules={modules}
                                    onChange={(value) => {
                                      prop.setFieldValue('detail', value)

                                      /* calculateCharacterCount(value)
                                      setMessage(value) */
                                    }}
                                    ref={reactQuillRef}
                                    onKeyDown={checkCharacterCount}
                                  />
                                  {isRichTextCharacterLimitExceeded && (
                                    <div className='fv-plugins-message-container mt-2'>
                                      <div className='fv-help-block'>
                                        {intl.formatMessage(
                                          {
                                            id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD',
                                          },
                                          {length: descriptionLimit}
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {prop.errors.detail && prop.touched.detail && (
                                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                      <div className='fv-help-block'>{prop.errors.detail}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className='row mb-3'>
                                <div className='col-2'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'ITEM.STATUS'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Select whether the product is Active or Inactive to
                                          control its availability.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <Select
                                    className='custom-select'
                                    options={status}
                                    value={status.find((s) => s.value === prop.values.status)}
                                    onChange={(value) => {
                                      prop.setFieldValue('status', value?.value)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.status && prop.touched.status && (
                                      <div className='fv-help-block'>{prop.errors.status}</div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-3'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'ITEM.PUBLISH_DATE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Specify the date when the product (service or product)
                                          will be made available.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <Flatpickr
                                    className='form-control mb-2'
                                    disabled={!prop.values.isEditable}
                                    readOnly={!prop.values.isEditable}
                                    placeholder={
                                      prop.values.availableFrom ||
                                      intl.formatMessage({
                                        id: 'ITEM.PUBLISH_DATE',
                                      })
                                    }
                                    value={prop.values.availableFrom}
                                    options={{
                                      dateFormat: 'd-m-Y H:i',
                                      enableTime: true,
                                      time_24hr: true,
                                      //minDate: new Date().setHours(0, 0, 0, 0),
                                      minDate: moment().format('DD-MM-YYYY HH:mm'),
                                    }}
                                    onChange={(date) => {
                                      const _date = moment(date[0]).format('DD-MM-YYYY HH:mm')
                                      prop.setFieldValue('availableFrom', _date)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.availableFrom && prop.touched.availableFrom && (
                                      <div className='fv-help-block'>
                                        {prop.errors.availableFrom}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-3'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'ITEM.END_DATE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Enter the date when the product will no longer be
                                          available.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <Flatpickr
                                    className='form-control mb-2'
                                    disabled={!prop.values.isEditable}
                                    readOnly={!prop.values.isEditable}
                                    placeholder={
                                      prop.values.availableUntil ||
                                      intl.formatMessage({
                                        id: 'ITEM.END_DATE',
                                      })
                                    }
                                    value={prop.values.availableUntil}
                                    options={{
                                      dateFormat: 'd-m-Y H:i',
                                      enableTime: true,
                                      time_24hr: true,
                                    }}
                                    onChange={(date) => {
                                      const _date = moment(date[0]).format('DD-MM-YYYY HH:mm')
                                      prop.setFieldValue('availableUntil', _date)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.availableUntil && prop.touched.availableUntil && (
                                      <div className='fv-help-block'>
                                        {prop.errors.availableUntil}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-3'>
                                  <div
                                    className={`${
                                      !prop.values.isSubscriptionPackage ? 'd-none' : ''
                                    }`}
                                  >
                                    <label className='required form-label'>
                                      Duration
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Choose the duration your customers will have access to
                                            this service. You can change the default one-month
                                            setting if needed.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </label>

                                    <NumericFormat
                                      name='duration'
                                      className='form-control mb-2'
                                      placeholder='Months'
                                      // readOnly={!prop.values.isEditable}
                                      // disabled={!prop.values.isEditable}
                                      disabled={
                                        !prop.values.isSubscriptionPackage ||
                                        !prop.values.isEditable
                                      }
                                      readOnly={
                                        !prop.values.isSubscriptionPackage ||
                                        !prop.values.isEditable
                                      }
                                      decimalScale={0}
                                      min={1}
                                      maxLength={3}
                                      allowNegative={false}
                                      allowLeadingZeros={false}
                                      value={prop.values.duration}
                                      onValueChange={(values) => {
                                        prop.setFieldValue('duration', values.floatValue)
                                      }}
                                      max={999}
                                    />

                                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                      {prop.errors.duration && (
                                        <div className='fv-help-block'>{prop.errors.duration}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className='row mb-3'>
                                <div className='col-2'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({
                                      id: 'ITEM.CATEGORY',
                                    })}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Choose the appropriate category to classify the product.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <Select
                                    isDisabled={!prop.values.isEditable}
                                    className='custom-select'
                                    placeholder={intl.formatMessage({
                                      id: 'ITEM.CATEGORY',
                                    })}
                                    options={categories?.map((item) => ({
                                      value: item.id,
                                      label: item.name,
                                      status: item.status,
                                    }))}
                                    value={categories
                                      ?.filter((s) => s.id === prop.values.categoryID)
                                      ?.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                      }))}
                                    onChange={(value) => {
                                      prop.setFieldValue('categoryID', value?.value)
                                      prop.setFieldValue('subCategoryID', '')

                                      detailSet({
                                        ...detail,
                                        categoryID: value?.value || '',
                                      })
                                    }}
                                    isLoading={isLoadingCategories}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.categoryID && prop.touched.categoryID && (
                                      <div className='fv-help-block'>{prop.errors.categoryID}</div>
                                    )}
                                  </div>
                                </div>
                                <div className='col-2'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({
                                      id: 'PRODUCT.LIST.SUB_CATEGORY',
                                    })}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Select the appropriate sub-category to ensure the product
                                          is accurately classified under its main category.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <Select
                                    isDisabled={!prop.values.isEditable}
                                    className='custom-select'
                                    placeholder={intl.formatMessage({
                                      id: 'PRODUCT.LIST.SUB_CATEGORY',
                                    })}
                                    options={subCategories?.map((item) => ({
                                      value: item.id,
                                      label: item.name,
                                      status: item.status,
                                    }))}
                                    value={subCategories
                                      ?.filter((s) => s.id === prop.values.subCategoryID)
                                      ?.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                      }))}
                                    onChange={(value) => {
                                      prop.setFieldValue('subCategoryID', value?.value)
                                    }}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.subCategoryID && prop.touched.subCategoryID && (
                                      <div className='fv-help-block'>
                                        {prop.errors.subCategoryID}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-2'>
                                  <label className='required form-label'>
                                    {intl.formatMessage({id: 'ITEM.TYPE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={<Tooltip>Select the product type.</Tooltip>}
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <Select
                                    isDisabled={!prop.values.isEditable}
                                    id='itemTypeID'
                                    name='itemTypeID'
                                    className='custom-select'
                                    options={
                                      itemTypes?.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                      })) || []
                                    }
                                    value={itemTypes
                                      ?.filter((s) => s.id === prop.values.itemTypeID)
                                      ?.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                      }))}
                                    onChange={(value) => {
                                      prop.setFieldValue('itemTypeID', value?.value)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.itemTypeID && prop.touched.itemTypeID && (
                                      <div className='fv-help-block'>{prop.errors.itemTypeID}</div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-2'>
                                  <label className='form-label'>
                                    {intl.formatMessage({id: 'PRODUCT.INPUT.SKU_ID'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Enter a unique identifier for the product, useful for
                                          tracking purposes.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                  <Field
                                    disabled={!prop.values.isEditable}
                                    isDisabled={!prop.values.isEditable}
                                    type='text'
                                    name='sku'
                                    id='sku'
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'PRODUCT.INPUT.SKU_ID',
                                    })}
                                    maxLength={51}
                                    value={prop.values?.sku}
                                    onChange={(e) => {
                                      prop.setFieldValue('sku', e.target.value)
                                    }}
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.sku && prop.touched.sku && (
                                      <div className='fv-help-block'>{prop.errors.sku}</div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-2'>
                                  <div
                                    className={`${
                                      !prop.values.isSubscriptionPackage ? 'd-none' : ''
                                    }`}
                                  >
                                    <label className='form-label required'>
                                      Usage Limit
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Set how many times this service can be used after
                                            purchase.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </label>
                                    <NumericFormat
                                      type='text'
                                      name='usageLimit'
                                      id='usageLimit'
                                      className='form-control mb-2'
                                      placeholder='Usage Limit'
                                      maxLength={4}
                                      decimalScale={0}
                                      disabled={
                                        !prop.values.isSubscriptionPackage ||
                                        !prop.values.isEditable
                                      }
                                      readOnly={
                                        !prop.values.isSubscriptionPackage ||
                                        !prop.values.isEditable
                                      }
                                      allowNegative={false}
                                      value={prop.values?.usageLimit}
                                      onChange={(e) => {
                                        prop.setFieldValue('usageLimit', e.target.value)
                                      }}
                                    />
                                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                      {prop.errors.usageLimit && prop.touched.usageLimit && (
                                        <div className='fv-help-block'>
                                          {prop.errors.usageLimit}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className='d-flex flex-column gap-5 mt-5'>
                                <div>
                                  <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                    <Field
                                      isDisabled={!prop.values.isEditable}
                                      disabled={!prop.values.isEditable}
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={prop.values.directSale}
                                      value={prop.values.directSale}
                                      onChange={(e) => {
                                        prop.setFieldValue('directSale', e.target.checked)

                                        prop.setFieldValue('price', null)
                                        prop.setFieldValue('taxRate', 5)
                                        prop.setFieldValue('purchaseType', null)

                                        if (!e.target.checked) {
                                          prop.setFieldValue('price', 0.0)
                                          prop.setFieldValue('eStore', false)
                                          prop.setFieldValue('paymentLink', false)
                                          prop.setFieldValue('imagesData', null)
                                          prop.setFieldValue('isSubscriptionPackage', false)
                                          prop.setFieldValue('usageLimit', 1)
                                          prop.setFieldValue('duration', 1)
                                        }
                                      }}
                                    />
                                    <label className='required form-label p-0 m-0'>
                                      {intl.formatMessage({id: 'ITEM.DIRECT.SALE'})}

                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Enable this option to allow customers to purchase the
                                            product directly.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </label>
                                  </div>
                                </div>

                                <div>
                                  <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                    <Field
                                      className='form-check-input'
                                      type='checkbox'
                                      id='isSubscriptionPackage'
                                      name='isSubscriptionPackage'
                                      defaultChecked={true}
                                      checked={prop.values.isSubscriptionPackage}
                                      disabled={!prop.values.isEditable || !prop.values.directSale}
                                      onChange={(e) => {
                                        prop.setFieldValue(
                                          'isSubscriptionPackage',
                                          e.target.checked
                                        )

                                        if (!e.target.checked) {
                                          prop.setFieldValue('usageLimit', 1)
                                          prop.setFieldValue('duration', 1)
                                        }
                                      }}
                                    />
                                    <label className='required form-label p-0 m-0'>
                                      Product as Membership
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Enable this option to define a duration and usage limit
                                            for the product, allowing it to function like a
                                            membership.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </label>
                                  </div>
                                </div>

                                <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                  <Field
                                    className='form-check-input'
                                    type='checkbox'
                                    disabled={!prop.values.directSale || !prop.values.isEditable}
                                    isDisabled={!prop.values.isEditable}
                                    checked={prop.values.paymentLink}
                                    onChange={(e) => {
                                      prop.setFieldValue('paymentLink', e.target.checked)
                                    }}
                                  />
                                  <label className='required form-label p-0 m-0'>
                                    {intl.formatMessage({id: 'LABEL.PAYMENT.LINK'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Choose whether to generate a payment link for easy
                                          transactions.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                </div>
                                <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid d-flex align-items-center gap-3'>
                                  <Field
                                    className='form-check-input'
                                    type='checkbox'
                                    disabled={!prop.values.directSale || !prop.values.isEditable}
                                    isDisabled={!prop.values.isEditable}
                                    checked={prop.values.eStore}
                                    onChange={(e) => {
                                      prop.setFieldValue('eStore', e.target.checked)
                                    }}
                                  />
                                  <label className='required form-label p-0 m-0'>
                                    {intl.formatMessage({id: 'LABEL.E.STORE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Select if the product will be available in the online
                                          store.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {prop.values.directSale && (
                          <div className='card card-flush py-4'>
                            <div className='card-header pb-5'>
                              <div className='card-title m-0'>
                                <div>
                                  <h3 className='fw-bold mb-2 mt-5'>
                                    {intl.formatMessage({id: 'LABEL.PRICING.DETAILS'})}
                                  </h3>
                                  <p className='text-muted fw-normal fs-6'>
                                    Set the pricing and tax information for the product.
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className='card-body pt-0 w-100'>
                              <div className='row'>
                                <div className='col-4'>
                                  <label className='form-label'>
                                    {intl.formatMessage({id: 'PRODUCT.INPUT.PURCHASE_TYPE'})}

                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          Select how the service or product can be purchased.
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </label>

                                  <Select
                                    className='custom-select'
                                    isDisabled={!prop.values.isEditable}
                                    options={mockUpPurchaseTypeSelect}
                                    value={mockUpPurchaseTypeSelect.find(
                                      (s) => s.value === prop.values.purchaseType
                                    )}
                                    onChange={(value) => {
                                      prop.setFieldValue('purchaseType', value?.value)
                                    }}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.purchaseType && prop.touched.purchaseType && (
                                      <div className='fv-help-block'>
                                        {prop.errors.purchaseType}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className='col-4'>
                                  <div className='mb-10 fv-row fv-plugins-icon-container'>
                                    <label className='required form-label'>
                                      {intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}

                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Enter the full selling price of the product.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </label>

                                    <NumericFormat
                                      type='text'
                                      name='price'
                                      disabled={!prop.values.isEditable}
                                      readOnly={!prop.values.isEditable}
                                      decimalSeparator='.'
                                      thousandSeparator=','
                                      maxLength={14}
                                      fixedDecimalScale
                                      className='form-control mb-2'
                                      placeholder={intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}
                                      prefix='AED '
                                      value={prop.values?.price || 0.0}
                                      onValueChange={(values) => {
                                        prop.setFieldValue('price', values.floatValue || 0.0)
                                      }}
                                      decimalScale={2}
                                      allowNegative={false}
                                      allowLeadingZeros={false}
                                    />

                                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                      {prop.errors.price && prop.touched.price && (
                                        <div className='fv-help-block'>{prop.errors.price}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {prop.values.directSale && (
                          <div className='card card-flush py-4'>
                            <div className='card-header pb-5'>
                              <div className='card-title m-0'>
                                <div>
                                  <h3 className='fw-bold mb-2 mt-5'>Image Section</h3>
                                  <p className='text-muted fw-normal fs-6'>
                                    Upload a representative visual for the product to support
                                    accurate display across listings and detail views.
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className='card-body pt-0 w-100'>
                              <div className='d-flex gap-3'>
                                {[0, 1, 2].map((index) => (
                                  <div
                                    key={index}
                                    className='image-input image-input-outline border border-dashed bg-light d-flex align-items-center'
                                    style={{
                                      maxWidth: '150px',
                                      maxHeight: '150px',
                                      minWidth: '150px',
                                      minHeight: '150px',
                                      position: 'relative',
                                    }}
                                  >
                                    {prop.values.imagesData?.[index]?.imageFullUrl ||
                                    prop.values.imagesData?.[index]?.imageUrl ? (
                                      <img
                                        src={
                                          prop.values?.imagesData?.[index]?.imageFullUrl?.includes(
                                            'http'
                                          )
                                            ? prop.values.imagesData?.[index]?.imageFullUrl
                                            : `${process.env.REACT_APP_IMG_DOMAIN}${prop.values.imagesData?.[index]?.imageUrl}`
                                        }
                                        alt=''
                                        className='rounded'
                                        style={{
                                          objectFit: 'contain',
                                          width: '100%',
                                          height: '100%',
                                          maxHeight: '150px',
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className='d-flex align-items-center justify-content-center'
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                        }}
                                      >
                                        <i className='bi bi-image fs-4x'></i>
                                      </div>
                                    )}
                                    <button
                                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                      type='button'
                                      style={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '5px',
                                      }}
                                      onClick={() => handleImageUpload(index)}
                                    >
                                      <i className='ki-outline ki-pencil fs-7'></i>
                                    </button>
                                    {(prop.values.imagesData?.[index]?.imageFullUrl ||
                                      prop.values.imagesData?.[index]?.imageUrl) && (
                                      <button
                                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                        type='button'
                                        style={{
                                          position: 'absolute',
                                          top: '5px',
                                          right: '35px',
                                        }}
                                        onClick={() => {
                                          const filteredImages = prop.values.imagesData?.filter(
                                            (image: any, i: number) => i !== index
                                          )
                                          prop.setFieldValue('imagesData', filteredImages)
                                        }}
                                      >
                                        <i className='ki-outline ki-cross fs-7'></i>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {prop.errors.imagesData && prop.touched.imagesData && (
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  <div className='fv-help-block'>{prop.errors.imagesData}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className='card card-flush py-4'>
                          <div className='card-header'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>Additional Information</h3>
                                <p className='text-muted fw-normal fs-6'>
                                  Highlight the key advantages and value of the membership to inform
                                  potential customers.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className='card-body w-100 d-flex flex-column gap-5'>
                            <div className='row'>
                              <div
                                className='accordion accordion-icon-collapse w-100 d-flex flex-column'
                                id='kt_accordion_3'
                              >
                                {faqs
                                  .filter((item) => {
                                    if (!isTermsAndConditionsAvailable) {
                                      return item.title !== 'Terms and Conditions'
                                    } else {
                                      return true
                                    }
                                  })
                                  .map((item, index) => {
                                    return (
                                      <div className='row' key={index}>
                                        <div className='mb-5 accordion-item border-0'>
                                          <div
                                            className={`
                                                              accordion-header collapsed py-3 d-flex
                                                              `}
                                            data-bs-toggle='collapse'
                                            data-bs-target={`#kt_accordion_3_item_${index + 1}`}
                                          >
                                            <span className={`accordion-icon`}>
                                              <i className='ki-duotone ki-plus-square fs-3 accordion-icon-off'>
                                                <span className='path1'></span>
                                                <span className='path2'></span>
                                                <span className='path3'></span>
                                              </i>
                                              <i className='ki-duotone ki-minus-square fs-3 accordion-icon-on'>
                                                <span className='path1'></span>
                                                <span className='path2'></span>
                                              </i>
                                            </span>
                                            <h3
                                              className={`fs-4 fw-bold mb-0 ms-4  ${
                                                // @ts-ignore
                                                prop.errors.infoContentData?.[index]?.data
                                                  ? 'text-danger'
                                                  : ''
                                              }`}
                                            >
                                              {item.title}
                                            </h3>
                                            <span className='position-relative'>
                                              <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                  <Tooltip>
                                                    {
                                                      tooltips.find((t) => t.title === item.title)
                                                        ?.content
                                                    }
                                                  </Tooltip>
                                                }
                                              >
                                                <i className='bi bi-info-circle text-primary ms-1'></i>
                                              </OverlayTrigger>
                                            </span>
                                          </div>
                                          <div
                                            id={`kt_accordion_3_item_${index + 1}`}
                                            className='collapse fs-6 ps-10'
                                            data-bs-parent='#kt_accordion_3'
                                          >
                                            <div className='row py-5'>
                                              <div className='col-10'>
                                                {index === 0 && <QuillToolbar2 />}
                                                {index === 1 && <QuillToolbar3 />}
                                                {index === 2 && <QuillToolbar4 />}
                                                {index === 3 && <QuillToolbar5 />}
                                                {index === 4 && <QuillToolbar6 />}
                                                <ReactQuill
                                                  readOnly={!prop.values.isEditable}
                                                  placeholder={intl.formatMessage({
                                                    id: 'DESCRIPTION',
                                                  })}
                                                  value={prop.values.infoContentData?.[index]?.data}
                                                  onKeyDown={checkCharacterCount}
                                                  modules={
                                                    index === 0
                                                      ? modules_2
                                                      : index === 1
                                                      ? modules_3
                                                      : index === 2
                                                      ? modules_4
                                                      : index === 3
                                                      ? modules_5
                                                      : index === 4
                                                      ? modules_6
                                                      : modules_2
                                                  }
                                                  formats={formats}
                                                  onChange={(value) => {
                                                    prop.setFieldValue(
                                                      `infoContentData[${index}].data`,
                                                      value
                                                    )
                                                  }}
                                                />
                                              </div>
                                              {/* show error */}
                                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                                {prop.errors.infoContentData?.[index] &&
                                                  // @ts-ignore
                                                  prop.errors.infoContentData?.[index]?.data && (
                                                    <div className='fv-help-block'>
                                                      {/* @ts-ignore */}
                                                      {prop.errors.infoContentData?.[index]?.data}
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex justify-content-end mt-10'>
                        {/*  {!prop.values.isEditable && (
                          <a
                            href={`/members/product/${detail?.id}?isMembership=${prop.values.isSubscriptionPackage}`}
                            className='btn btn-text px-15'
                          >
                            {isSaving ? (
                              <span
                                className='spinner-border spinner-border-sm'
                                role='status'
                                aria-hidden='true'
                              ></span>
                            ) : (
                              <>Members</>
                            )}
                          </a>
                        )} */}
                        <button type='submit' className='btn btn-dark px-15'>
                          {isSaving ? (
                            <span
                              className='spinner-border spinner-border-sm'
                              role='status'
                              aria-hidden='true'
                            ></span>
                          ) : (
                            intl.formatMessage({id: 'GLOBAL.SAVE'})
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </>
          )
        }}
      </Formik>
    )
  }

  function MembersWhoPurchased() {
    return (
      <div>
        <MembersByItem />
      </div>
    )
  }
}
